import { useCallback, useEffect, useRef, useState } from 'react'
import { pick } from 'lodash-es'
import {
  useRealAmap,
  labelMarkersPlugin,
  polysPlugin,
  boundsPlugin,
  Layer,
  iconMap,
} from '@ow/maps/lib'
import { useProjectStore, useTbViewStore } from '@vt/hooks'
import { mergeQueries, checkPoint, getDeepPath } from '@ow/utils'
import {
  defaultMapConfig,
  getBoundFilter,
  getLegendFilter,
  getGeoFieldSlug,
} from './utils'

const useVtAmap = (mapId) => {
  const getVtProject = useProjectStore((state) => state.getVtProject)
  const { amapKey = '0e4f7b5e424e7db93755cb5d48e71a75' } = getVtProject()
  const {
    ditto: tbDitto,
    filters,
    mapConfig = defaultMapConfig,
  } = useTbViewStore((state) => pick(state, ['ditto', 'filters', 'mapConfig']))

  const [bounds, setBounds] = useState()

  const boundsLayer = useRef(new Layer(null, {}))
  const labelMarkerLayer = useRef(new Layer(null, {}))
  const polygonsLayer = useRef(new Layer(null, {}))

  const { getApis, inited } = useRealAmap(mapId, {
    amapKey,
    plugins: [
      boundsPlugin(),
      labelMarkersPlugin(),
      polysPlugin({ cacheKey: 'id' }),
    ],
    zoom: mapConfig.zoom,
    zooms: mapConfig.zooms,
    center: mapConfig.center,
  })

  // 获取坐标数据
  const loadData = useCallback(
    async (finalFilters, image) => {
      const data = await tbDitto.fetchTbData({
        filters: finalFilters,
        pageSize: 1000,
      })

      const icon = {
        type: 'image',
        // 图片 url
        image,
        // 图片尺寸
        size: [30, 30],
      }

      const geoFields = getGeoFieldSlug(mapConfig.mainLayer?.geoFieldSlug)
      const markerData = []
      const polygonData = []

      data.forEach((item) => {
        geoFields.forEach((key) => {
          if (checkPoint(item[key])) {
            markerData.push({ ...item[key], id: item.objectId })
          } else if (Array.isArray(item[key])) {
            item[key].forEach((geo, i) => {
              if (geo.type === 'Point') {
                markerData.push({
                  ...item,
                  position: geo.coordinates,
                  id: `${item.objectId}-${key}-${i}`,
                })
              } else if (geo.type === 'Polygon') {
                polygonData.push({
                  ...item,
                  path: getDeepPath(geo.coordinates),
                  id: `${item.objectId}-${key}-${i}`,
                })
              }
            })
          }
        })
      })

      labelMarkerLayer.current.render(markerData, { icon, autoFit: false })
      polygonsLayer.current.render(polygonData)
    },
    [tbDitto]
  )

  useEffect(() => {
    if (inited) {
      const { initLabelMarker, initPolys, initBounds } = getApis()
      initLabelMarker(labelMarkerLayer.current)
      initBounds(boundsLayer.current, { onChange: setBounds })
      initPolys(polygonsLayer.current)

      setBounds(boundsLayer.current.getBounds())
    }
  }, [inited])

  useEffect(() => {
    if (!inited) return
    if (!bounds) return

    mapConfig.mainLayer.legends.forEach((legend) => {
      loadData(
        mergeQueries(
          mergeQueries(getBoundFilter(bounds), getLegendFilter(legend.queries)),
          filters
        ),
        iconMap[legend.markerIcon]
      )
    })
  }, [inited, bounds, filters])
}

export default useVtAmap
